<template>
  <div class="quiz-container">
    <!-- Śnieżynki w tle -->
    <div class="snowflakes">
      <div
        class="snowflake"
        v-for="i in 10"
        :key="i"
        :style="snowflakeStyle(i)"
      ></div>
    </div>

    <h1>🎄 Quiz Programistyczny 🎄</h1>

    <div class="questions-list">
      <div
        v-for="(question, index) in questions"
        :key="index"
        class="question-card"
        :class="{ completed: userAnswers[index] !== null }"
      >
        <h3>{{ question.text }}</h3>
        <div class="options">
          <label
            v-for="(option, i) in question.options"
            :key="i"
            class="option"
            :class="{
              incorrect:
                quizCompleted &&
                userAnswers[index] === option &&
                option !== question.answer,
              correct: quizCompleted && option === question.answer,
              selected: userAnswers[index] === option && !quizCompleted,
            }"
          >
            <input
              type="radio"
              :name="`question-${index}`"
              :value="option"
              v-model="userAnswers[index]"
              :disabled="quizCompleted"
            />
            <span class="option-text">{{ option }}</span>
          </label>
        </div>
      </div>
    </div>

    <div class="quiz-actions">
      <button
        @click="validateQuiz"
        v-if="!quizCompleted"
        class="submit-btn"
        :disabled="userAnswers.includes(null)"
      >
        Sprawdź odpowiedzi
      </button>

      <div class="result" v-if="quizCompleted">
        <p>
          Twój wynik:
          <strong>{{ correctAnswers }}/{{ questions.length }}</strong>
        </p>
        <div class="progress-bar">
          <div
            class="progress-fill"
            :style="{ width: (correctAnswers / questions.length) * 100 + '%' }"
          ></div>
        </div>
      </div>

      <div class="action-buttons" v-if="quizCompleted">
        <button
          v-if="correctAnswers < questions.length"
          @click="resetQuiz"
          class="reset-btn"
        >
          Spróbuj ponownie
        </button>
        <button @click="claimReward" class="claim-btn">
          {{
            correctAnswers === questions.length ? "Powrót do menu" : "Kontynuuj"
          }}
        </button>
      </div>

      <!-- Modal z gratulacjami -->
      <div v-if="correctAnswers === questions.length" class="congrats-modal">
        <div class="modal-content">
          <h2>🎉 Gratulacje! 🎉</h2>
          <p>Udało Ci się rozwiązać quiz bezbłędnie!</p>
          <button @click="claimReward" class="modal-btn">Powrót do menu</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

// Funkcja do generowania losowych śnieżynek
const snowflakeStyle = (i) => {
  const size = Math.random() * 8 + 3;
  const left = Math.random() * 100;
  const animationDuration = Math.random() * 10 + 5;
  const delay = Math.random() * 5;
  return {
    width: `${size}px`,
    height: `${size}px`,
    left: `${left}%`,
    animationDuration: `${animationDuration}s`,
    animationDelay: `${delay}s`,
  };
};

const questions = ref([
  {
    text: "Co oznacza skrót HTML?",
    options: [
      "HighTech Machine Learning",
      "Hyperlink and Text Markup Language",
      "HyperText Markup Language",
      "HyperTurbo Markup Language",
    ],
    answer: "HyperText Markup Language",
  },
  {
    text: "Który z tych języków jest używany w przeglądarkach?",
    options: ["Python", "JavaScript", "C++", "Ruby"],
    answer: "JavaScript",
  },
  {
    text: "Czym jest Vue.js?",
    options: [
      "Biblioteką CSS",
      "Frameworkiem JavaScript",
      "Edytorem tekstu",
      "Systemem operacyjnym",
    ],
    answer: "Frameworkiem JavaScript",
  },
  {
    text: "Co oznacza skrót CSS?",
    options: [
      "Crazy Stylish Syntax",
      "Creative Style Scripts",
      "Cascading Style System",
      "Cascading Style Sheets",
    ],
    answer: "Cascading Style Sheets",
  },
  {
    text: "Który z tych języków jest używany w backendzie aplikacji?",
    options: ["C#", "VisualBasic", "CSS", "XML"],
    answer: "C#",
  },
  {
    text: "Który z tych języków jest pierwowzorem C#?",
    options: ["Java", "Python", "JavaScript", "HTML"],
    answer: "Java",
  },
  {
    text: "Co oznacza skrót JSON?",
    options: [
      "JavaScript Online Network",
      "JavaScript Object Notation",
      "Java Syntax Object Notation",
      "JavaScript Oriented Notation",
    ],
    answer: "JavaScript Object Notation",
  },
  {
    text: "Który z tych frameworków jest używany w Pythonie?",
    options: ["Django", "React", "Vue", "Angular"],
    answer: "Django",
  },
  {
    text: "Który z tych języków jest używany do stylowania stron?",
    options: ["CSS", "Python", "C++", "Java"],
    answer: "CSS",
  },
  {
    text: "Który z tych języków jest używany do tworzenia aplikacji mobilnych?",
    options: ["HTML", "Swift", "CSS", "SQL"],
    answer: "Swift",
  },
]);

const userAnswers = ref(questions.value.map(() => null));
const correctAnswers = ref(0);
const quizCompleted = ref(false);

const validateQuiz = () => {
  correctAnswers.value = questions.value.reduce((score, question, index) => {
    return score + (userAnswers.value[index] === question.answer ? 1 : 0);
  }, 0);
  quizCompleted.value = true;
};

const claimReward = () => {
  document.cookie = `puzzle1=completed; max-age=3600`;
  router.push("/home");
};

const resetQuiz = () => {
  userAnswers.value = questions.value.map(() => null);
  correctAnswers.value = 0;
  quizCompleted.value = false;
};
</script>

<style scoped>
.quiz-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  position: relative;
  overflow: hidden;
}

/* Śnieżynki */
.snowflakes {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
}

.snowflake {
  position: absolute;
  background: white;
  border-radius: 50%;
  opacity: 0.7;
  animation: fall linear infinite;
}

@keyframes fall {
  0% {
    transform: translateY(-10px) rotate(0deg);
  }
  100% {
    transform: translateY(100vh) rotate(360deg);
  }
}

/* Świąteczne kolory */
h1 {
  color: #d32f2f;
  text-align: center;
  margin-bottom: 30px;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-card {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.question-card.completed {
  border-left-color: #2e7d32; /* Zielony akcent */
}

h3 {
  margin-top: 0;
  color: #d32f2f; /* Czerwony nagłówek */
  font-weight: 600;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 15px;
}

.option {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9f9f9;
}

.option:hover {
  background: #f0f0f0;
}

.option.selected {
  background: #ebf9ff; /* Jasnoczerwone tło */
  border: 1px solid #2f9fd3;
}

.option.correct {
  background: #e8f5e9; /* Jasnozielone tło */
  border: 1px solid #2e7d32;
  color: #2e7d32;
}

.option.incorrect {
  background: #ffebee;
  border: 1px solid #ef9a9a;
  color: #c62828;
}

.option input[type="radio"] {
  margin-right: 10px;
}

.option-text {
  flex: 1;
}

.quiz-actions {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.submit-btn {
  background-color: #d32f2f; /* Czerwony przycisk */
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s;
}

.submit-btn:hover {
  background-color: #b71c1c;
}

.submit-btn:disabled {
  background-color: #b0bec5;
  cursor: not-allowed;
}

.result {
  text-align: center;
  margin: 20px 0;
}

.result strong {
  color: #d32f2f;
  font-size: 1.2em;
}

.progress-bar {
  height: 8px;
  width: 100%;
  background: #e0e0e0;
  border-radius: 4px;
  margin-top: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #2e7d32; /* Zielony pasek postępu */
  transition: width 0.5s ease;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.reset-btn,
.claim-btn {
  padding: 10px 25px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.reset-btn {
  background-color: #ff9800;
  color: white;
}

.reset-btn:hover {
  background-color: #e68a00;
}

.claim-btn {
  background-color: #2e7d32; /* Zielony przycisk */
  color: white;
}

.claim-btn:hover {
  background-color: #1b5e20;
}

.congrats-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 500px;
  width: 90%;
}

.modal-btn {
  background-color: #2e7d32; /* Zielony przycisk */
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;
  transition: all 0.3s;
}

.modal-btn:hover {
  background-color: #1b5e20;
}
</style>
